import { reactive } from 'vue';
import { DossierId } from '@/dossier/models/AbstractDossier';
import DossierContext from '@/dossier/models/DossierContext';
import AuthService from '@/general/services/AuthService';
import ServiceEvents, { TireDeleted, TireReplaced, TiresSwapped } from '@/shared/services/events/serviceEvents';
import { isPresent, present } from '@/shared/utils/typescript';
import TireClient from '../client/TireClient';
import TireMakeOption from '../constants/TireMakeOption';
import { AxleData } from '../converters/tireConverters';
import { AxleId } from '../models/Axle';
import AxlesData from '../models/AxleFormData';
import Tire, { TireEditData } from '../models/Tire';
import TireContainer, { RepairKitData } from '../models/TireContainer';
import TireContext from '../models/TireContext';
import TireMake from '../models/TireMake';
import TirePosition from '../models/TirePosition';

class AxleService {
  async loadByDossier(dossierContext: DossierContext): Promise<TireContext> {
    const container = await TireClient.loadAll(dossierContext.dossier.id);
    return reactive(new TireContext(dossierContext, container)) as TireContext;
  }

  async updateAxleLayout(context: TireContext, addedAxles: Array<AxleData>): Promise<void> {
    await TireClient.updateAxleLayout(context.dossierContext.dossier.id, addedAxles);
    await this.doLoadContainer(context);
  }

  async doLoadContainer(context: TireContext): Promise<void> {
    context.container = await TireClient.loadAll(context.dossierContext.dossier.id);
    context.container.sanitize();
  }

  async removeAxle(context: TireContext, axleId: AxleId): Promise<void> {
    await TireClient.removeAxle(context.dossierContext.dossier.id, axleId);
    context.container.axles.splice(context.container.axles.findIndex(a => a.id === axleId), 1);
    context.container.recalculateAxleIndexes();
  }

  async editTire(context: TireContext, data: TireEditData, position: TirePosition, mounted: boolean, isReplacement: boolean): Promise<void> {
    const positions = context.container.getTargetPositions(position, data.target);
    const positionIds = positions.map(p => p.id);
    if (data.make === TireMakeOption.OTHER) data.tire.make = data.otherMake as TireMake;
    else data.tire.make = data.make;
    context.container = present(await TireClient.editTire(context.dossierContext.dossier.id, data.tire, isReplacement, mounted, positionIds));
    context.container.sanitize();
    if (isReplacement) await ServiceEvents.fire(new TireReplaced(positionIds, mounted));
  }

  async removeTire(dossierId: DossierId, position: TirePosition, mounted: boolean): Promise<void> {
    const tireIndex = position.tires.findIndex(t => t.isMounted === mounted);

    await TireClient.removeTire(dossierId, present(position.tires[tireIndex].id));

    position.tires.splice(tireIndex, 1);
    position.tires.push(mounted ? Tire.withMountedStatus() : new Tire());

    await ServiceEvents.fire(new TireDeleted(position.id, mounted));
  }

  async updateMeasurements(dossierId: DossierId, data: AxlesData, container: TireContainer): Promise<void> {
    await TireClient.updateMeasurements(dossierId, data, AuthService.getCurrentUser());
    container.storageLocation = data.storageLocation;
  }

  async swap(context: TireContext): Promise<void> {
    const container = context.container;
    await TireClient.swap(context.dossierContext.dossier.id);

    await ServiceEvents.fire(new TiresSwapped());
    container.getAllPositions().forEach(p => p.tires.forEach(t => t.isMounted = !t.isMounted));
  }

  async updateRepairKitStatus(data: RepairKitData, context: TireContext): Promise<void> {
    await TireClient.updateRepairKit(context.container.vehicleId, data);
    context.container.setRepairKitStatus(data);
  }

  async updateTireNote(context: TireContext, tire: Tire, content: string): Promise<void> {
    const dossierId = context.dossierContext.dossier.id;
    const tireId = present(tire.id);
    const noteId = tire.note?.id;

    if (isPresent(noteId) && content) {
      await TireClient.updateTireNote(dossierId, tireId, noteId, content);
      present(tire.note).content = content;
    } else if (content) {
      tire.note = await TireClient.addTireNote(dossierId, tireId, content);
    } else if (isPresent(noteId)) {
      await TireClient.removeTireNote(dossierId, tireId, noteId);
      tire.note = null;
    }
  }

  async updateBrakeNote(context: TireContext, position: TirePosition, content: string): Promise<void> {
    const dossierId = context.dossierContext.dossier.id;
    const positionId = position.id;
    const brake = present(position.brake);
    const noteId = brake.note?.id;

    if (isPresent(noteId) && content) {
      await TireClient.updateBrakeNote(dossierId, positionId, noteId, content);
      present(brake.note).content = content;
    } else if (content) {
      brake.note = await TireClient.addBrakeNote(dossierId, positionId, content);
    } else if (isPresent(noteId)) {
      await TireClient.removeBrakeNote(dossierId, positionId, noteId);
      brake.note = null;
    }
  }
}

export default new AxleService();